/**
 * Client-side companion to GA4 tracking for the pre-auth download funnel
 * (platform tile click, auth-provider choice, auth attempt failure).
 *
 * GA4 events (see `lib/analytics`) give us the funnel in the GA4 property;
 * this posts the same signal to Composer via the portal BFF so the same
 * three stages also show up on the internal admin KPI dashboard. Fire-and-
 * forget by design — a dropped funnel-analytics beacon must never block or
 * visibly fail the user-facing download / signup flow.
 */

export type NrsDownloadFunnelEventType =
  | "download_page_view"
  | "download_os_tile_click_windows_x86_64"
  | "download_os_tile_click_macos_apple_silicon_arm64"
  | "download_os_tile_click_macos_intel_x86_64"
  | "download_os_tile_click_linux_x86_64"
  | "auth_provider_selected"
  | "auth_attempt_failed"
  | "auth_callback_success"
  | "auth_flow_abandoned"
  | "mobile_download_email_modal_shown"
  | "mobile_download_email_submitted"
  | "mobile_download_email_link_opened";

export type RecordNrsDownloadFunnelEventParams = {
  eventType: NrsDownloadFunnelEventType;
  /** Required for OS tile click events; ignored otherwise. */
  platformSlug?: string;
  /** Required for "auth_provider_selected" / "auth_attempt_failed"; ignored otherwise. */
  provider?: "google" | "github" | "neuraville";
  /** Optional, only meaningful for "auth_attempt_failed". */
  reason?: string;
  /** Stable per-attempt correlation id for click -> auth -> session flow joins. */
  flowId?: string;
  /**
   * Per-visit correlation id minted when `/nrs/download` loads. Unlike `flowId`
   * (one per tile click), this stays constant for the whole visit, so every
   * funnel stage from page view to upgrade counts the same unit: one visitor.
   */
  visitId?: string;
  /** Page path the event originated from (e.g. `/nrs/download`). */
  source?: string;
  /** Visitor form factor for the OS-tile click stack on Visual KPI. */
  clientFormFactor?: "mobile" | "desktop" | "unknown";
  /**
   * Required for "mobile_download_email_link_opened". Bridges the mobile visit
   * that requested the email to this later visit — see
   * `nrs_desktop_download_link_emails` in Composer. Never derived from, or
   * reversible to, an email address.
   */
  dlRef?: string;
  /** Optional sanitized, non-PII diagnostics for auth failures. */
  metadata?: Record<string, string | number | boolean | null>;
};

/**
 * Posts one funnel event to `/api/nrs/download-funnel-events`. Never throws;
 * failures are swallowed so a slow/blocked network never affects the UI.
 */
export function recordNrsDownloadFunnelEvent(
  params: RecordNrsDownloadFunnelEventParams
): void {
  if (typeof window === "undefined" || typeof fetch !== "function") {
    return;
  }
  const payload: Record<string, unknown> = {
    event_type: params.eventType,
  };
  if (params.platformSlug) {
    payload.platform_slug = params.platformSlug;
  }
  if (params.provider) {
    payload.provider = params.provider;
  }
  if (params.reason) {
    payload.reason = params.reason;
  }
  if (params.flowId) {
    payload.flow_id = params.flowId;
  }
  if (params.visitId) {
    payload.visit_id = params.visitId;
  }
  if (params.source) {
    payload.source = params.source;
  }
  if (params.clientFormFactor) {
    payload.client_form_factor = params.clientFormFactor;
  }
  if (params.dlRef) {
    payload.dl_ref = params.dlRef;
  }
  if (params.metadata && Object.keys(params.metadata).length > 0) {
    payload.metadata = params.metadata;
  }
  const body = JSON.stringify(payload);
  try {
    // `sendBeacon` is more resilient than fetch during immediate route changes
    // (OAuth callback -> redirect), so prefer it when available.
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      const blob = new Blob([body], { type: "application/json" });
      if (navigator.sendBeacon("/api/nrs/download-funnel-events", blob)) {
        return;
      }
    }
    void fetch("/api/nrs/download-funnel-events", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      cache: "no-store",
      keepalive: true,
    }).catch(() => {
      // Fire-and-forget: analytics must never surface as a user-facing error.
    });
  } catch {
    // Same as above — synchronous fetch construction failures are ignored.
  }
}
